import React from "react";

/**
 * SendVertex — official static brand mark.
 *
 * Reads as "Send[✓]ertex" — the "V" of Vertex is replaced by a rounded-square
 * checkbox containing a checkmark. Bold dark type. Optional tagline below.
 *
 * No animation. Static, print-safe, screenshot-safe.
 */
export function SendVertexLogo({
  size = 40,
  showWordmark = true,
  showTagline = false,
  className = "",
  wordmarkClassName = "text-2xl",
  taglineClassName = "text-[10px]",
  variant = "auto", // "auto" | "light" | "dark"
}) {
  const isDark = variant === "dark";
  const strokeColor = isDark ? "#ffffff" : "#0f172a";
  const textColor = isDark ? "text-white" : "text-slate-950";
  const taglineColor = isDark ? "text-slate-300" : "text-slate-700";

  // Icon size scales with the wordmark height — use `size` as the icon height in px.
  return (
    <div
      className={`inline-flex flex-col items-center ${className}`}
      aria-label="SendVertex"
      data-testid="sendvertex-logo"
    >
      {showWordmark && (
        <div
          className={`font-display font-black tracking-tight leading-none select-none inline-flex items-center ${textColor} ${wordmarkClassName}`}
        >
          <span>Send</span>
          <CheckboxV size={size} stroke={strokeColor} />
          <span>ertex</span>
        </div>
      )}

      {showTagline && (
        <div
          className={`mt-2 font-mono uppercase tracking-[0.16em] ${taglineColor} ${taglineClassName}`}
        >
          Intelligent email. Exceptional deliverability.
        </div>
      )}
    </div>
  );
}

/**
 * Rounded-square checkbox containing a checkmark — sits inline as the "V"
 * of "Vertex". Uses `currentColor`-style stroke prop so it inherits the
 * wordmark color for light/dark variants.
 */
function CheckboxV({ size = 40, stroke = "#0f172a" }) {
  // The icon should be visually the same height as a capital letter of the
  // wordmark — roughly 0.85 × the font size for a font-display Black weight.
  const iconSize = Math.round(size * 0.85);
  return (
    <svg
      viewBox="0 0 32 32"
      width={iconSize}
      height={iconSize}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block mx-[0.05em] shrink-0 align-middle"
      style={{ verticalAlign: "middle" }}
      aria-hidden="true"
    >
      {/* Rounded-square outline */}
      <rect
        x="2.5"
        y="2.5"
        width="27"
        height="27"
        rx="4.5"
        stroke={stroke}
        strokeWidth="3"
        fill="none"
      />
      {/* Checkmark inside */}
      <path
        d="M 8 17 L 14 23 L 24 10"
        stroke={stroke}
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

export default SendVertexLogo;
